/**
 * Auth Store - Token 认证状态管理（Pinia 内存态）
 *
 * 职责：
 * - 存储 token、userInfo（仅内存，不持久化）
 * - 提供 bootstrap 页面初始化流程
 * - 提供失效处理
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  parseLoginParams,
  parseTicket,
  resolveLoginMode,
  stashTicketAndCleanUrl,
  type UserInfo,
} from '../authService'
// 登入 / 登出 / 余額 / 币种全部走 src/api/*（useHttp）。
// 註：fetchToken（dev acc 登入）api 版只從 body 取 token，不再讀 Response Header 的
//     Authorization；dev 後端需把 token 放在回應 body。
import { fetchToken, loginWithTicket, guestLogin, logout as logoutApi } from '../api/users'
import type { LoginResult } from '../api/users/types'
import { fetchBalance } from '../api/wallets'
import { fetchBets } from '../api/game'
import { ApiError } from '../api/http'
import { toUserMessage } from '../api/errorMessage'
import { i18n } from '../i18n'
import type { BetsResult } from '../api/game/types'
import { readGameIdPreservingCase } from '../composables/useGameId'

/**
 * 從 URL 解析 game_code（ticket 登入要帶給後端，識別這張 ticket 是進哪一款遊戲）
 *
 * 走 useGameId 的解析（全專案唯一解析點，支援 `?gameId=` query /
 * `/play/gameId=x` 路徑 / local-games 三種格式）。
 * ⚠️ 之前只讀 query —— 路徑式 URL（`/play/gameId=mjhl`）下 ticket 登入會帶
 * **空 gameCode**（2026-07-30 修）。
 *
 * 🛑 **這裡刻意用 `readGameIdPreservingCase`（不轉小寫）**：後端是拿對接商下發
 *    連結上的原字串比對的，轉小寫會對不上（實際收過 `?gameId=MJHL`）。
 *    專案其他地方一律用 `readGameId()` 的小寫值 —— 兩者的差別見該函式註解。
 */
function parseGameCode(): string {
  return readGameIdPreservingCase(new URL(window.location.href))?.trim() || ''
}

export const useAuthStore = defineStore('auth', () => {
  // ========== State ==========
  const token = ref<string | null>(null)
  const userInfo = ref<UserInfo | null>(null)
  const ready = ref(false)
  const error = ref<string | null>(null)
  const loading = ref(false)
  // 当前账户币种信息（来自 v1/{gameCode}/bets）
  const bets = ref<BetsResult | null>(null)

  // 单飞锁：避免并发触发多次登录
  let bootstrapPromise: Promise<boolean> | null = null

  // ========== Actions ==========

  /**
   * 执行登录 —— 依 `resolveLoginMode()`（authService 单一决策点）分派：
   *   - `'guest'`  → guestLogin()（demo，config.json.env==='demo'）
   *   - `'devAcc'` → fetchToken()（URL 帶 `?acc=` + ENABLED_DEV_LOGIN）
   *   - `'ticket'` → loginWithTicket()（正式）
   *   - `null`     → 缺 ticket，报错
   * token 一律只放本 store 记忆体（含 guest），不落 storage。
   */
  async function login(): Promise<boolean> {
    token.value = null
    userInfo.value = null
    ready.value = false
    error.value = null

    const mode = resolveLoginMode()
    if (!mode) {
      error.value = i18n.global.t('common.errors.missingTicket')
      return false
    }
    try {
      let result: LoginResult
      if (mode === 'guest') {
        result = await guestLogin()
      } else if (mode === 'devAcc') {
        result = await fetchToken(parseLoginParams())
      } else {
        result = await loginWithTicket({ ticket: parseTicket()!, gameCode: parseGameCode() })
      }
      token.value = result.token
      userInfo.value = { userId: String(result.userId), balance: 0 }
      // ticket 模式：登入成功即把 ticket 收進 sessionStorage、清出網址列
      //（續期用 parseTicket() 的 storage fallback；非 ticket 模式 no-op）。
      stashTicketAndCleanUrl()
      return true
    } catch (e: unknown) {
      // 登入端點自己回 401 = 憑證（ticket / 帳號）無效或已失效。這條路徑**不會**
      // 觸發重新登入（http 攔截器對 skipAuth 端點不啟動 recovery，避免遞迴），
      // 所以直接就是終態；把後端的 'Unauthorized' 原樣丟到畫面對玩家沒有意義。
      if (e instanceof ApiError && e.status === 401) {
        error.value = i18n.global.t(
          mode === 'ticket' ? 'common.errors.ticketInvalid' : 'common.errors.loginFailed',
        )
      } else {
        // 逾時 / 網路 / 5xx 走分類文案；其餘落到 loginFailed。
        // ⚠️ 不可用 e.message —— 那是 'Request timed out' 這類給工程師看的英文。
        error.value = toUserMessage(e, 'common.errors.loginFailed')
      }
      return false
    }
  }

  /**
   * 同步餘額到 userInfo.balance。
   *
   * 也是「這個 token 到底能不能用」的驗證動作（bootstrap 拿它當關卡）。
   *
   * @param duringRecovery 由 refreshAuth（續期流程本身）呼叫時要傳 true —— 這支 401 時
   *   **不得**觸發／等待續期，否則會 await 到正在等自己的那個 promise（死鎖）。
   *   一般呼叫傳 false：balance 是 GET，http 層會自動續期 + 重發，這裡通常直接拿到結果。
   * @returns 'ok' | 'unauthorized'（401）| 'failed'（其他錯誤）
   */
  async function syncBalance(duringRecovery = false): Promise<'ok' | 'unauthorized' | 'failed'> {
    try {
      const balanceData = await fetchBalance({ skipAuthRecovery: duringRecovery })
      if (userInfo.value) userInfo.value.balance = balanceData.balance ?? 0
      return 'ok'
    } catch (e: unknown) {
      return e instanceof ApiError && e.status === 401 ? 'unauthorized' : 'failed'
    }
  }

  /** 取幣種資訊（失敗不阻塞，bets 留空） */
  async function loadBets(gameCode?: string | null): Promise<void> {
    try {
      bets.value = gameCode ? await fetchBets({ gameCode }) : null
    } catch {
      bets.value = null
    }
  }

  /**
   * 页面初始化流程：login -> fetchBalance -> fetchBets
   * 支持单飞（避免并发重复触发）
   *
   * @param gameCode 當前 gameId —— bets 已改 per-game 端點 `/v1/{gameCode}/bets`，
   *                 缺值則跳過 bets（不阻塞登入）。
   */
  async function bootstrap(gameCode?: string | null): Promise<boolean> {
    // 单飞：如果已有进行中的认证，返回该 Promise
    if (bootstrapPromise) {
      return bootstrapPromise
    }

    bootstrapPromise = (async () => {
      loading.value = true
      error.value = null
      ready.value = false
      token.value = null
      userInfo.value = null
      bets.value = null

      try {
        // 1. 登录换取 token
        const loginSuccess = await login()
        if (!loginSuccess) {
          return false
        }

        // 2. 取余额，同时验证 token 有效性
        if (!token.value) {
          error.value = i18n.global.t('common.errors.missingLogin')
          return false
        }
        //    401 不用在這裡特別處理：balance 是 GET，http 攔截器會自己「續期 → 帶新 token
        //    重發」，成功的話這裡直接拿到餘額。真的救不回來才會 reject 到這（此時
        //    onTerminalFail 已呼叫 invalidate()，訊息設好了）。
        const state = await syncBalance()

        if (state !== 'ok') {
          // invalidate() 已設過訊息就不要蓋掉（那個對玩家更精確）
          if (!error.value) error.value = i18n.global.t('common.errors.verifyFailed')
          token.value = null
          userInfo.value = null
          return false
        }

        // 3. 取余额币种（`/v1/{gameCode}/bets`）。失败不阻塞登录流程，bets 留空
        await loadBets(gameCode)

        ready.value = true
        return true
      } finally {
        loading.value = false
        bootstrapPromise = null
      }
    })()

    return bootstrapPromise
  }

  /**
   * 静默续期：重新登录获取新 token，不重置 ready 状态，不触发加载动画。
   * 依 resolveLoginMode 分派（**三種登入模式都可续期**）：
   *   - `'devAcc'` → 重新 fetchToken
   *   - `'guest'`  → 重新 guestLogin
   *     ⚠️ guest 每次登入 = **一位全新试玩玩家**（餘額歸零）。demo 環境可接受，
   *        但這也是續期後一定要重抓餘額的原因之一。
   *   - `'guest'` → 重新 guestLogin。⚠️ **會拿到全新試玩玩家**（後端 guest/login 無參數、
   *     每次發新帳號），demo 餘額歸零重來 —— 2026-07-30 與產品確認**維持此行為**
   *     （試玩無感續玩優先於餘額連續性）；若日後要同帳號重登，需後端支援帶 user_id 換 token。
   *   - `'ticket'` → 重新 loginWithTicket（**後端已改為同一張 ticket 可重複換 token**；
   *        舊版一次性、~3 分鐘有效的假設已不適用。ticket 從 URL 讀，全程不清除）
   *   - `null`     → 沒有任何可用憑證，無法续期
   *
   * 續期成功後會 **重抓一次餘額**：token 換了新 session（guest 更是直接換人），
   * 畫面上的舊餘額會是錯的。抓失敗不影響续期結果（仍算成功）。
   */
  async function refreshAuth(): Promise<boolean> {
    const mode = resolveLoginMode()
    if (!mode) return false
    try {
      let result: LoginResult
      if (mode === 'devAcc') {
        result = await fetchToken(parseLoginParams())
      } else if (mode === 'guest') {
        result = await guestLogin()
      } else {
        result = await loginWithTicket({ ticket: parseTicket()!, gameCode: parseGameCode() })
      }
      token.value = result.token
      if (userInfo.value) {
        userInfo.value.userId = String(result.userId)
      } else {
        // bootstrap 失敗路徑會把 userInfo 清成 null，補建一個才存得下餘額
        userInfo.value = { userId: String(result.userId), balance: 0 }
      }
      // 新 token 已就位 → 重抓餘額（失敗不影響续期成功與否）。
      // 必須傳 duringRecovery=true：我們正在續期流程「裡面」，這支若 401 不能再等續期。
      await syncBalance(true)
      return true
    } catch {
      return false
    }
  }

  /**
   * 调用登出接口（无 body；useHttp 自动从本 store 携带当前 token）
   */
  async function logout(): Promise<void> {
    await logoutApi()
  }

  /**
   * 处理 token 失效（401 等场景）
   */
  function invalidate() {
    token.value = null
    userInfo.value = null
    bets.value = null
    ready.value = false
    error.value = i18n.global.t('common.errors.sessionExpired')
  }

  /**
   * 重置状态（不设置错误）
   */
  function reset() {
    clear()
  }

  /**
   * 清空所有状态
   */
  function clear() {
    token.value = null
    userInfo.value = null
    bets.value = null
    ready.value = false
    error.value = null
    loading.value = false
  }

  return {
    // State
    token,
    userInfo,
    ready,
    error,
    loading,
    bets,
    // Actions
    login,
    bootstrap,
    refreshAuth,
    syncBalance,
    logout,
    invalidate,
    reset,
    clear,
  }
})
